import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Check, Play, ExternalLink } from 'lucide-react';

import { API_URL } from '../../utils/apiUrl';

const VendorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [embedModal, setEmbedModal] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prodsRes, subsRes] = await Promise.all([
        axios.get(`${API_URL}/api/vendor/products`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/vendor/submissions`).catch(() => ({ data: [] })),
      ]);
      setProducts(prodsRes.data);
      setSubmissions(subsRes.data);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmbed = async (productId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/vendor/products/${productId}/embed`);
      setEmbedModal(data);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Embed kodu alınamadı';
      toast.error(msg);
    }
  };

  const copyEmbed = () => {
    if (embedModal?.embed_code) {
      navigator.clipboard.writeText(embedModal.embed_code);
      setCopied(true);
      toast.success('Embed kodu kopyalandı!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const STATUS_LABELS = {
    submitted: "Başvuru Yapıldı", received: "Ürün Alındı",
    under_review: "İnceleniyor", scored: "Puanlandı", published: "Yayınlandı",
    rejected: "Başvuru Reddedildi",
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-lg font-bold text-neutral-500">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="vendor-dashboard">
      {/* Header */}
      <header className="bg-neutral-900 text-white">
        <div className="px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-black tracking-tight">TESTETTiK</h1>
            <span className="text-xs text-neutral-400 font-medium">Satıcı Paneli</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-neutral-400">{user?.name} ({user?.vendor_brand})</span>
            <button onClick={handleLogout} className="text-xs font-semibold text-neutral-300 hover:text-white transition"
              data-testid="vendor-logout">Çıkış</button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-neutral-200">
        <div className="px-6 flex gap-0">
          <button onClick={() => setActiveTab('products')}
            className={`py-3 px-6 text-sm font-bold border-b-2 transition ${activeTab === 'products' ? 'border-neutral-900 text-neutral-900' : 'border-transparent text-neutral-500 hover:text-neutral-800'}`}
            data-testid="vendor-tab-products">Ürünlerim</button>
          <button onClick={() => setActiveTab('submissions')}
            className={`py-3 px-6 text-sm font-bold border-b-2 transition ${activeTab === 'submissions' ? 'border-neutral-900 text-neutral-900' : 'border-transparent text-neutral-500 hover:text-neutral-800'}`}
            data-testid="vendor-tab-submissions">Başvurularım</button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-6">
        {activeTab === 'products' && (
          <div>
            <h2 className="text-2xl font-black tracking-tight mb-6" data-testid="vendor-products-title">Ürünlerim</h2>
            {products.length === 0 ? (
              <div className="text-center py-16 text-neutral-400">
                <p className="text-lg font-bold mb-2">Henüz ürününüz yok</p>
                <p className="text-sm">Ürün gönderiniz değerlendirildikten sonra burada görünecektir.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map(product => (
                  <div key={product.id} className="border border-neutral-200 rounded-md overflow-hidden" data-testid={`vendor-product-${product.id}`}>
                    <div className="relative aspect-video bg-neutral-900">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Play className="w-10 h-10 text-white/30" />
                        </div>
                      )}
                      <div className="absolute top-3 right-3 px-3 py-1.5 rounded-sm shadow-lg" style={{ backgroundColor: product.score_color }}>
                        <span className="text-white font-mono font-black text-lg">{product.score?.toFixed(1)}</span>
                        <span className="text-white/70 font-mono text-xs">/10</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <span className="inline-block px-2.5 py-1 text-white text-xs font-bold rounded-sm mb-2" style={{ backgroundColor: product.score_color }}>
                        {product.verdict_display}
                      </span>
                      <h3 className="text-base font-black mb-1">{product.name}</h3>
                      <p className="text-sm text-neutral-500 mb-1">{product.brand}</p>
                      <div className="flex items-center gap-2 mt-3">
                        <span className={`px-2 py-0.5 text-xs font-bold rounded ${product.published ? 'bg-green-100 text-green-800' : 'bg-neutral-100 text-neutral-600'}`}>
                          {product.published ? 'Yayında' : 'Taslak'}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-bold rounded ${product.badge_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          Rozet: {product.badge_active ? 'Aktif' : 'Pasif'}
                        </span>
                      </div>
                      {product.score >= 7 && product.published && product.badge_active && (
                        <button onClick={() => fetchEmbed(product.id)}
                          className="mt-3 w-full bg-neutral-900 text-white px-4 py-2 text-xs font-bold rounded hover:bg-neutral-800 transition flex items-center justify-center gap-2"
                          data-testid={`embed-btn-${product.id}`}>
                          <ExternalLink className="w-3.5 h-3.5" /> Embed Kodunu Al
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'submissions' && (
          <div>
            <h2 className="text-2xl font-black tracking-tight mb-6" data-testid="vendor-submissions-title">Başvurularım</h2>
            {submissions.length === 0 ? (
              <div className="text-center py-16 text-neutral-400">
                <p className="text-lg font-bold mb-2">Henüz başvurunuz yok</p>
                <Link to="/submit" className="text-sm text-[#1C3B2A] font-semibold hover:underline">Ürün Gönder →</Link>
              </div>
            ) : (
              <div className="border border-neutral-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-900 text-white">
                    <tr>
                      <th className="text-left p-3 font-bold">Ürün</th>
                      <th className="text-left p-3 font-bold">Marka</th>
                      <th className="text-left p-3 font-bold">Kategori</th>
                      <th className="text-left p-3 font-bold">Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((sub, i) => (
                      <tr key={sub.id} className={i % 2 === 0 ? 'bg-white' : 'bg-neutral-50'} data-testid={`vendor-sub-${sub.id}`}>
                        <td className="p-3 font-medium">{sub.product_name}</td>
                        <td className="p-3 text-neutral-600">{sub.brand}</td>
                        <td className="p-3 text-neutral-500">{sub.category}</td>
                        <td className="p-3">
                          <span className="px-2.5 py-1 text-xs font-bold rounded bg-neutral-100 text-neutral-700">
                            {STATUS_LABELS[sub.status] || sub.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Embed Modal */}
      {embedModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={() => setEmbedModal(null)}>
          <div className="bg-white rounded-md max-w-lg w-full p-6 max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="embed-modal">
            <h3 className="text-lg font-black mb-2">Embed Kodu</h3>
            {embedModal.badge_expired && (
              <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm font-medium mb-3">
                Rozet süresi dolmuş — embed kodunda uyarı görünecektir.
              </div>
            )}
            <p className="text-xs text-neutral-500 mb-3">Bu kodu web sitenize ekleyerek test sonucunu gösterebilirsiniz.</p>
            <div className="bg-neutral-50 border border-neutral-200 rounded p-3 mb-4 max-h-40 overflow-auto">
              <code className="text-xs font-mono break-all text-neutral-700">{embedModal.embed_code}</code>
            </div>
            <div className="flex gap-3">
              <button onClick={copyEmbed}
                className="flex-1 bg-neutral-900 text-white px-4 py-2.5 text-sm font-bold rounded hover:bg-neutral-800 transition flex items-center justify-center gap-2"
                data-testid="copy-embed-modal">
                {copied ? <><Check className="w-4 h-4" /> Kopyalandı</> : <><Copy className="w-4 h-4" /> Kodu Kopyala</>}
              </button>
              <button onClick={() => setEmbedModal(null)}
                className="px-4 py-2.5 text-sm font-bold border border-neutral-300 rounded hover:bg-neutral-100 transition">Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-neutral-900 text-white py-8 px-6 mt-16">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm text-neutral-500">&copy; 2026 TESTETTiK. Tüm hakları saklıdır.</p>
        </div>
      </footer>
    </div>
  );
};

export default VendorDashboard;
